import { TNotesResponseScheme } from '../../../packages/api-models/monolith-python/ajax/notes';
import { updateNoteApi, IApiParamsSaveNote } from '../../api';
import { EEntityType } from '../../types/filters';
import { ENotificationStyle } from '../../types/notifications';
import { TThunkAction } from '../../types/redux';
import { INoteFetchingInfo } from '../../types/results';
import { convertToFavoriteEntityType } from '../../utils/favoriteEntity';
import { notify } from '../notifications';
import { setOfferNote, setOfferNoteFailed, setOfferNoteLoading } from '../offersByRealtyOfferId';

export const NOTES_ERRORS = {
  DELETE: 'Не удалось удалить заметку. Проверьте подключение к интернету и попробуйте еще раз',
  SAVE: 'Заметка не сохранена. Проверьте соединение с интернетом и попробуйте еще раз',
};

export enum ENoteActionsTypes {
  SetFetchNote = 'offers/set_fetch_note',
  SetFetchSuccessNote = 'offers/set_fetch_success_note',
  SetFetchFailNote = 'offers/set_fetch_fail_note',
  StartEditNote = 'favoriteEntities/edit_note',
  CancelEditNote = 'favoriteEntities/cancel_edit_note',
  UpdateEntityNote = 'favoriteEntities/update_entity_note',
  SetPropForDeletingNote = 'favoriteEntities/set_prop_for_deleting_note',
  RemovePropForDeletingNote = 'favoriteEntities/remove_prop_for_deleting_note',
}

export type TNotesActions = IEditNote | ICancelEditNote;

export interface IEditNote {
  type: ENoteActionsTypes.StartEditNote;
  payload: IEditNotePayload;
}

export interface IEditNotePayload {
  [key: number]: INoteFetchingInfo;
}

/* istanbul ignore next */
export function startEditNote(entityId: number): IEditNote {
  return {
    type: ENoteActionsTypes.StartEditNote,
    payload: {
      [entityId]: {
        isFetching: false,
        isFetchSuccess: false,
        isFetchFail: false,
      },
    },
  };
}

export interface ICancelEditNote {
  type: ENoteActionsTypes.CancelEditNote;
  payload: ICancelEditNotePayload;
}

export interface ICancelEditNotePayload {
  entityId: number;
}

/* istanbul ignore next */
export function cancelEditNote(entityId: number): ICancelEditNote {
  return {
    type: ENoteActionsTypes.CancelEditNote,
    payload: { entityId },
  };
}

export interface IUpdateNoteParams {
  entityId: number;
  comment?: string | null;
  successCallback?: () => void;
}

/* istanbul ignore next */
export function updateNote({ entityId, comment, successCallback }: IUpdateNoteParams): TThunkAction {
  return (dispatch, getState, context) => {
    const { httpApi, logger } = context;
    const {
      page: { subdomain },
      results: { favoriteEntities },
      map: { isOpen: isMapOpen },
    } = getState();
    const entity = favoriteEntities.find(({ entityId: id }) => id === entityId);
    const paramComment = comment ? comment : '';

    dispatch(setFetchNote(entityId));

    if (isMapOpen) {
      dispatch(setOfferNoteLoading());
    }

    if (!entity) {
      dispatch(notify(NOTES_ERRORS.SAVE, ENotificationStyle.Error));

      return;
    }

    const { dealType, entityType } = entity;
    const favoriteEntityType = entityType;
    const apiParams: IApiParamsSaveNote = {
      oid: entityId,
      text: paramComment,
      action: 'save',
      csrf_token: '', //всегда ''
      dealType,
      offerType: convertToFavoriteEntityType(favoriteEntityType as unknown as EEntityType),
      httpApi,
      subdomain,
    };

    updateNoteApi(apiParams)
      .then((response: TNotesResponseScheme) => {
        if (response.statusCode === 200) {
          dispatch(setFetchSuccessNote(entityId));
          dispatch(cancelEditNote(entityId));
          dispatch(updateEntityNote(entityId, paramComment));

          if (isMapOpen) {
            dispatch(setOfferNote({ realtyOfferId: entityId, comment: paramComment }));
          }

          if (successCallback) {
            successCallback();
          }
        } else {
          throw response;
        }
      })
      .catch(err => {
        logger.warning(err);
        dispatch(setFetchFailNote(entityId));
        dispatch(notify(NOTES_ERRORS.SAVE, ENotificationStyle.Error));

        if (isMapOpen) {
          dispatch(setOfferNoteFailed());
        }
      });
  };
}

/* istanbul ignore next */
export function deleteNote({ entityId, comment }: IUpdateNoteParams): TThunkAction {
  return (dispatch, getState, context) => {
    const { httpApi, logger } = context;
    const {
      page: { subdomain },
      results: { favoriteEntities },
    } = getState();
    const entity = favoriteEntities.find(({ entityId: id }) => id === entityId);
    const blankComment = comment || '';

    dispatch(setPropForDeletingNote(entityId));

    if (!entity) {
      dispatch(notify(NOTES_ERRORS.DELETE, ENotificationStyle.Error));

      return;
    }

    const { dealType, entityType } = entity;
    const favoriteEntityType = entityType;
    const apiParams: IApiParamsSaveNote = {
      oid: entityId,
      text: blankComment,
      action: 'delete',
      csrf_token: '', //всегда ''
      dealType,
      offerType: convertToFavoriteEntityType(favoriteEntityType as unknown as EEntityType),
      httpApi,
      subdomain,
    };

    updateNoteApi(apiParams)
      .then((response: TNotesResponseScheme) => {
        if (response.statusCode === 200) {
          dispatch(removePropForDeletingNote(entityId));
          dispatch(cancelEditNote(entityId));
          dispatch(updateEntityNote(entityId, blankComment));
        } else {
          throw response;
        }
      })
      .catch(err => {
        logger.warning(err);
        dispatch(removePropForDeletingNote(entityId));
        dispatch(notify(NOTES_ERRORS.DELETE, ENotificationStyle.Error));
      });
  };
}

export interface ISetPropForDeletingNote {
  type: ENoteActionsTypes.SetPropForDeletingNote;
  payload: ISetPropForDeletingNotePayload;
}

export interface ISetPropForDeletingNotePayload {
  entityId: number;
}

/* istanbul ignore next */
export function setPropForDeletingNote(entityId: number): ISetPropForDeletingNote {
  return {
    type: ENoteActionsTypes.SetPropForDeletingNote,
    payload: { entityId },
  };
}

export interface IRemovePropForDeletingNote {
  type: ENoteActionsTypes.RemovePropForDeletingNote;
  payload: ISetPropForDeletingNotePayload;
}

/* istanbul ignore next */
export function removePropForDeletingNote(entityId: number): IRemovePropForDeletingNote {
  return {
    type: ENoteActionsTypes.RemovePropForDeletingNote,
    payload: { entityId },
  };
}

export interface IUpdateEntityNote {
  type: ENoteActionsTypes.UpdateEntityNote;
  payload: IUpdateEntityNotePayload;
}

export interface IUpdateEntityNotePayload {
  entityId: number;
  comment: string;
}

/* istanbul ignore next */
export function updateEntityNote(entityId: number, comment: string): IUpdateEntityNote {
  return {
    type: ENoteActionsTypes.UpdateEntityNote,
    payload: { comment, entityId },
  };
}

export interface ISetFetchNote {
  type: ENoteActionsTypes.SetFetchNote;
  payload: ISetFetchNotePayload;
}

export interface ISetFetchNotePayload {
  entityId: number;
}

/* istanbul ignore next */
export function setFetchNote(entityId: number): ISetFetchNote {
  return {
    type: ENoteActionsTypes.SetFetchNote,
    payload: { entityId },
  };
}

export interface ISetFetchSuccessNote {
  type: ENoteActionsTypes.SetFetchSuccessNote;
  payload: ISetFetchNoteSuccessPayload;
}

export interface ISetFetchNoteSuccessPayload {
  entityId: number;
}

/* istanbul ignore next */
export function setFetchSuccessNote(entityId: number): ISetFetchSuccessNote {
  return {
    type: ENoteActionsTypes.SetFetchSuccessNote,
    payload: { entityId },
  };
}

export interface ISetFetchFailNote {
  type: ENoteActionsTypes.SetFetchFailNote;
  payload: ISetFetchNoteFailPayload;
}

export interface ISetFetchNoteFailPayload {
  entityId: number;
}

/* istanbul ignore next */
export function setFetchFailNote(entityId: number): ISetFetchFailNote {
  return {
    type: ENoteActionsTypes.SetFetchFailNote,
    payload: { entityId },
  };
}
